import marqii from '@/assets/images/marqii.svg'
import { Button } from '@/components/buttons/Button'
import { ConditionalComponent } from '@/components/ConditionalComponent/ConditionalComponent'
import { Flex } from '@/components/Flex'
import { IconButton } from '@/components/IconButton'
import { Image } from '@/components/Image'
import { ImpersonatingBanner } from '@/components/ImpersonatingBanner'
import { QuickNavigationButton } from '@/components/layouts/Header/QuickNavigationButton'
import { UpgradePackageButton } from '@/components/layouts/Header/UpgradePackageButton'
import { UserProfileDropdown } from '@/components/layouts/Header/UserProfileDropdown'
import { PaymentIssueWarningBanner } from '@/components/PaymentIssueWarningBanner/PaymentIssueWarningBanner'
import { Space } from '@/components/Space'
import { useDarkTheme } from '@/contexts/DarkThemeContext'
import { DashboardExperiments } from '@/experiments'
import { useSelfServeAndPermissions } from '@/hooks/permissions/useSelfServeAndPermissions'
import { useIsMobile } from '@/hooks/useBreakpoint'
import { useFlag } from '@/hooks/useFlag'
import { useFullLayout } from '@/hooks/useFullLayout'
import { useToken } from '@/hooks/useToken'
import { PATHS } from '@/routes/PATHS'
import { usePremiumFeaturesStatus } from '@/stores/permissions/premiumFeatures'
import { Layout } from 'antd'
import { MenuIcon, MoonIcon, SunIcon } from 'lucide-react'
import { Link } from 'react-router'
import { BeamerWidget } from './Beamer/BeamerWidget'
import styles from './styles.module.scss'

export const HEADER_TEST_ID = 'header-test-id'

export interface HeaderProps {
  onMenuClick?: () => void
  onQuickNavigationClick?: () => void
  hideBeamer?: boolean
}

export function Header({ onMenuClick, onQuickNavigationClick, hideBeamer }: HeaderProps) {
  const isMobile = useIsMobile()
  const { isUnlocked, isDarkTheme, toggleDarkMode } = useDarkTheme()
  const { token } = useToken()

  const shouldEnforceBillingFoundations = useFlag(
    DashboardExperiments.shouldEnforceBillingFoundations,
  )

  const isFullLayout = useFullLayout()

  const { canDoUpgrade } = useSelfServeAndPermissions()

  const premiumFeaturesStatus = usePremiumFeaturesStatus()

  const hasQuickNavigationFeature = useFlag(DashboardExperiments.quickNavigation)

  const shouldBlockAccount =
    premiumFeaturesStatus &&
    premiumFeaturesStatus === 'SUSPENDED' &&
    shouldEnforceBillingFoundations

  const shouldShowBeamer = !shouldBlockAccount && !hideBeamer

  return (
    <div>
      <ImpersonatingBanner />
      <PaymentIssueWarningBanner />

      <Layout.Header data-testid={HEADER_TEST_ID} className={styles.header}>
        <Flex align="center" gap="1rem">
          <ConditionalComponent condition={isMobile && isFullLayout}>
            <IconButton size="large" icon={<MenuIcon size={20} />} onClick={onMenuClick} />
          </ConditionalComponent>

          <Link to={PATHS.homePage}>
            <Space align="center">
              <Image
                src={marqii}
                width="100px"
                alt="Marqii logo"
                style={{ filter: isDarkTheme ? 'invert(1)' : 'none' }}
              />
            </Space>
          </Link>
        </Flex>
        <Flex align="center" gap={isMobile ? 10 : 25}>
          <ConditionalComponent condition={isUnlocked}>
            <Button
              shape="circle"
              type="text"
              icon={
                isDarkTheme ? (
                  <MoonIcon color={token.colorText} size={token.fontSizeLG} />
                ) : (
                  <SunIcon color={token.colorText} size={token.fontSizeLG} />
                )
              }
              onClick={toggleDarkMode}
              style={{ lineHeight: 0 }}
            />
          </ConditionalComponent>
          <ConditionalComponent condition={canDoUpgrade}>
            <UpgradePackageButton />
          </ConditionalComponent>
          <ConditionalComponent condition={shouldShowBeamer}>
            <BeamerWidget />
          </ConditionalComponent>
          <ConditionalComponent condition={hasQuickNavigationFeature}>
            <QuickNavigationButton onClick={() => onQuickNavigationClick?.()} />
          </ConditionalComponent>
          <UserProfileDropdown />
        </Flex>
      </Layout.Header>
    </div>
  )
}
